import "../pages/AdminMobileResponsive.css";
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN, assetUrl } from "../api";
import { filterFiles, isAttachmentFile, MAX_ATTACHMENTS, uploadErrorMessage } from "../uploadUtils";
import AdminLayout from "../components/AdminLayout";
import {
  ChevronRight, ArrowLeft, FileText, User, Mail, MapPin, Phone, Car,
  Settings2, Mic, Play, Pause, Pencil, Paperclip, X, Download, History, AlertTriangle, Activity,
} from "lucide-react";
import { isAdmin, ROLE_LABEL } from "../auth";
import { AttentionDot } from "./RequestRow";
import {
  money, fmtDate, fmtDateTime, REQUEST_STATUS_STYLE, ADMIN_QUOTE_STATUS, quoteExpiry, waitedFor, RESPONSE_TARGET_MIN, minutesSince,
  numberVersions, replacedFrom,
} from "../quoteUtils";
import QuoteCompare from "./QuoteCompare";

const fmtTime = (d) => new Date(d).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
const timeAgo = (d) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(d)) / 60000));
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

export default function AdminRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [req, setReq] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [comments, setComments] = useState([]);
  const [versions, setVersions] = useState([]); // all quotations, newest first
  const [showVersions, setShowVersions] = useState(false);
  const [activity, setActivity] = useState([]);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const threadEndRef = useRef(null);

  const loadQuotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/requests/${id}/quotations`);
      setVersions(Array.isArray(data) ? data : []);
    } catch {
      setVersions([]);
    }
  }, [id]);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get(`/requests/${id}/activity`);
      setActivity(Array.isArray(data) ? data : []);
    } catch {
      setActivity([]);
    }
  }, [id]);

  const loadRequest = useCallback(async () => {
    try {
      const { data } = await api.get(`/requests/${id}`);
      setReq(data);
    } catch (e) {
      setLoadError(e.response?.data?.message || "Could not load this request.");
    }
  }, [id]);

  useEffect(() => {
    setReq(null);
    setLoadError("");
    loadRequest();
    loadQuotes();
    loadActivity();
    api.get(`/requests/${id}/comments`).then((r) => setComments(r.data)).catch(() => setComments([]));
  }, [id, loadRequest, loadQuotes, loadActivity]);

  useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [comments]);

  // ---------- live updates: comments, quote changes, status changes ----------
  useEffect(() => {
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("token") } });
    socket.emit("join", `request:${id}`);
    socket.on("comment:new", (comment) => {
      if (String(comment.requestId) !== String(id)) return;
      setComments((prev) => (prev.some((m) => m._id === comment._id) ? prev : [...prev, comment]));
      loadActivity();
    });
    socket.on("quote:update", (q) => {
      if (String(q?.requestDocId) !== String(id)) return;
      loadQuotes();
      loadRequest();
      loadActivity();
    });
    socket.on("request:update", (r) => {
      if (String(r?._id) === String(id)) setReq(r);
    });
    api.patch(`/admin/conversations/${id}/read`).catch(() => { });
    return () => {
      socket.emit("leave", `request:${id}`);
      socket.disconnect();
    };
  }, [id, loadQuotes, loadRequest, loadActivity]);

  const addFiles = (list) => {
    const { accepted, problems } = filterFiles(list, { room: MAX_ATTACHMENTS - files.length, accept: isAttachmentFile });
    if (problems.length) alert(problems.join("\n"));
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  };

  const addComment = async () => {
    if (sending || (!note.trim() && files.length === 0)) return;
    setSending(true);
    const fd = new FormData();
    fd.append("text", note);
    files.forEach((f) => fd.append("attachments", f));
    try {
      const { data } = await api.post(`/requests/${id}/comments`, fd);
      if (data?._id) setComments((prev) => (prev.some((m) => m._id === data._id) ? prev : [...prev, data]));
      setNote("");
      setFiles([]);
    } catch (err) {
      alert(uploadErrorMessage(err, "Could not add comment."));
    } finally {
      setSending(false);
    }
  };

  if (loadError) {
    return <AdminLayout><main className="p-6"><div className="bg-white rounded-xl p-12 text-center text-red-500">{loadError}</div></main></AdminLayout>;
  }
  if (!req) {
    return <AdminLayout><main className="p-6"><div className="bg-white rounded-xl p-12 text-center text-slate-400">Loading…</div></main></AdminLayout>;
  }

  const photos = [...(req.partPhotos || []), ...(req.vehicleImage ? [req.vehicleImage] : []), ...(req.additionalVehicleImages || [])];
  const latest = versions.find((q) => q.isLatest) || versions[0] || null;
  const hasQuote = !!latest;
  const quoteBtnLabel = hasQuote ? "Edit Quote" : "Create Quote";
  const lastMsg = comments[comments.length - 1];
  const customerWaiting = lastMsg && lastMsg.authorRole !== "admin";
  const waitingOverdue = customerWaiting && minutesSince(lastMsg.createdAt) >= RESPONSE_TARGET_MIN;

  return (
    <AdminLayout>
      <main className="p-6 agp-admin-responsive agp-admin-request-detail">
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          {/* Header row */}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <button onClick={() => navigate("/admin/requests")} className="hover:text-slate-700">Requests</button>
                <ChevronRight size={14} />
                <span className="text-slate-700">{req.requestId}</span>
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <h1 className="text-3xl font-extrabold text-slate-800">{req.requestId}</h1>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase ${REQUEST_STATUS_STYLE[req.status] || "bg-slate-100 text-slate-500"}`}>
                  {req.status}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Submitted {timeAgo(req.createdAt)} ({fmtDateTime(req.createdAt)})
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/admin/requests")}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                <ArrowLeft size={16} /> Back to Requests
              </button>
              <button onClick={() => navigate(`/admin/requests/${id}/quote`)}
                className="px-5 py-2.5 rounded-lg bg-slate-800 text-white font-semibold text-sm hover:bg-slate-900">
                {quoteBtnLabel}
              </button>
            </div>
          </div>

          {customerWaiting && (
            <div className={`mt-5 flex items-center gap-3 rounded-xl px-4 py-3 text-sm ${waitingOverdue ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900"}`}>
              <AttentionDot overdue={waitingOverdue} />
              <div className="flex-1">
                <b>{lastMsg.authorName || "The customer"}</b> is waiting for a reply ({waitedFor(lastMsg.createdAt)}).
                {waitingOverdue && ` That's past the ${RESPONSE_TARGET_MIN}-minute response time.`}
              </div>
            </div>
          )}

          {req.quoteStatus === "revision_requested" && (
            <div className="mt-5 flex items-start gap-3 bg-purple-50 text-purple-800 rounded-xl px-4 py-3 text-sm">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" />
              <div className="flex-1">The customer asked for changes to this quote. Check the comments, then update and resend it.</div>
              <button onClick={() => navigate(`/admin/requests/${id}/quote`)} className="font-semibold underline shrink-0">Revise quote</button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* LEFT */}
            <div className="space-y-6">
              <QuotePanel
                request={req}
                latest={latest}
                versions={versions}
                showVersions={showVersions}
                setShowVersions={setShowVersions}
                onEdit={() => navigate(`/admin/requests/${id}/quote`)}
              />

              {/* Customer & Vehicle */}
              <section className="border border-gray-100 rounded-2xl p-5">
                <h3 className="font-bold text-slate-800 mb-4">Customer &amp; Vehicle</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 font-bold text-slate-800">
                      <span className="grid place-items-center w-8 h-8 rounded-full bg-blue-100 text-blue-600"><User size={16} /></span>
                      {req.customerName}
                    </div>
                    <div className="mt-3 space-y-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> {req.customerContact || "—"}</div>
                      {req.customerEmail && <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {req.customerEmail}</div>}
                      {req.address && <div className="flex items-center gap-2"><MapPin size={14} className="text-slate-400" /> {req.address}</div>}
                      {req.createdBy && isAdmin() && (
                        <button onClick={() => navigate(`/admin/customers/${req.createdBy}`)} className="text-blue-600 text-xs font-semibold hover:underline">
                          View customer profile
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 font-bold text-slate-800">
                      <span className="grid place-items-center w-8 h-8 rounded-full bg-blue-100 text-blue-600"><Car size={16} /></span>
                      Vehicle
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-slate-500">
                      <div>VIN: {req.vin || "—"}</div>
                      <div>REGO: {req.rego || "—"}</div>
                      <div>Identified by: {req.vehicleMethod === "photo" ? "Photo" : "Manual entry"}</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Part Required */}
              <section className="border border-gray-100 rounded-2xl p-5">
                <h3 className="font-bold text-slate-800 mb-4">Part Required</h3>
                <div className="flex gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-blue-100 text-blue-600 shrink-0"><Settings2 size={18} /></span>
                  <div>
                    <div className="font-bold text-slate-800 text-lg">
                      {req.partRequired || "Part"}{req.partNumber ? ` (${req.partNumber})` : ""}
                    </div>
                    <p className="text-slate-500 text-sm mt-1 whitespace-pre-line">{req.notes || "—"}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-5 mb-2">
                  <h4 className="font-bold text-slate-800 text-sm uppercase">Uploaded Photos ({photos.length})</h4>
                </div>
                {photos.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {photos.map((p, i) => (
                      <a key={i} href={assetUrl(p)} target="_blank" rel="noreferrer">
                        <img src={assetUrl(p)} alt="" className="w-28 h-24 object-cover rounded-xl border hover:opacity-90" />
                        {/* {assetUrl(p)} */}
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No photos uploaded.</p>
                )}

                {req.voiceNotes?.length > 0 && (
                  <>
                    <h4 className="font-bold text-slate-800 text-sm uppercase mt-5 mb-2">Voice Notes ({req.voiceNotes.length})</h4>
                    <div className="space-y-2">
                      {req.voiceNotes.map((v, i) => (
                        <>
                          <VoiceNote key={i} src={assetUrl(v)} index={i + 1} />
                          {/* {assetUrl(v)} */}
                        </>
                      ))}
                    </div>
                  </>
                )}

                <h4 className="font-bold text-slate-800 text-sm uppercase mt-5 mb-2">Customer Notes</h4>
                <div className="bg-blue-50/60 rounded-xl p-4 text-sm text-slate-600 italic">
                  {req.notes ? `"${req.notes}"` : "No notes."}
                </div>
              </section>
            </div>

            {/* RIGHT — comments */}
            <div className="space-y-6">
              <section className="border border-gray-100 rounded-2xl p-5 flex flex-col">
                <h3 className="font-bold text-slate-800 mb-1">Comments</h3>
                <p className="text-xs text-slate-400 mb-4">Messages here are visible to the customer.</p>
                <div className="flex-1 space-y-4 overflow-y-auto max-h-[460px] pr-1">
                  {comments.map((c) => {
                    const mine = c.authorRole === "admin";
                    return (
                      <div key={c._id} className={`flex ${mine ? "justify-end" : "justify-start"} gap-2`}>
                        {!mine && (
                          <span className="grid place-items-center w-7 h-7 rounded-full bg-slate-200 text-slate-600 text-xs font-bold shrink-0">
                            {(c.authorName?.[0] || "?").toUpperCase()}
                          </span>
                        )}
                        <div className={`max-w-[75%] ${mine ? "text-right" : ""}`}>
                          {c.text && (
                            <div className={`inline-block px-4 py-2.5 rounded-2xl text-sm text-left whitespace-pre-line ${mine ? "bg-blue-100 text-slate-700" : "bg-slate-100 text-slate-700"}`}>
                              {c.text}
                            </div>
                          )}
                          {c.attachments?.length > 0 && (
                            <div className={`mt-2 space-y-2 ${mine ? "flex flex-col items-end" : ""}`}>
                              {c.attachments.map((a, i) => <Attachment key={i} a={a} />)}
                            </div>
                          )}
                          <div className="text-[11px] text-slate-400 mt-1">
                            {c.authorName || (mine ? "Staff" : "Customer")}
                            {mine && c.staffRole && <span className="ml-1 px-1.5 py-px rounded bg-slate-100 text-slate-500 text-[10px] font-semibold">{ROLE_LABEL[c.staffRole]}</span>}
                            {" · "}{fmtTime(c.createdAt)}
                          </div>
                        </div>
                        {mine && (
                          <span className="grid place-items-center w-7 h-7 rounded-full bg-slate-800 text-white text-xs font-bold shrink-0">
                            {(c.authorName || "S").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {comments.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No comments yet.</p>}
                  <div ref={threadEndRef} />
                </div>

                <div className="mt-4">
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {files.map((f, i) => (
                        <span key={i} className="flex items-center gap-1 text-xs bg-slate-100 rounded-full px-3 py-1">
                          {f.name}
                          <button aria-label={`Remove ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}><X size={12} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2">
                    <input
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && !sending && addComment()}
                      placeholder="Reply to the customer..."
                      className="flex-1 text-sm focus:outline-none"
                    />
                    <button onClick={() => fileRef.current?.click()} aria-label="Attach files" className="text-slate-400 hover:text-slate-600">
                      <Paperclip size={18} />
                    </button>
                    <input ref={fileRef} type="file" multiple hidden
                      accept="image/*,application/pdf,audio/*,video/*"
                      onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                  </div>
                  <div className="flex justify-end mt-2">
                    <button onClick={addComment} disabled={sending}
                      className="px-5 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 disabled:opacity-60">
                      {sending ? "Sending…" : "Send Reply"}
                    </button>
                  </div>
                </div>
              </section>

              <ActivityLog items={activity} />
            </div>
          </div>
        </div>
      </main>
    </AdminLayout>
  );
}

/* --- Who did what on this request --- */
function ActivityLog({ items }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 8);
  return (
    <section className="border border-gray-100 rounded-2xl p-5">
      <h3 className="font-bold text-slate-800 mb-1 flex items-center gap-2"><Activity size={16} /> Activity</h3>
      <p className="text-xs text-slate-400 mb-4">Who created, quoted and replied on this request.</p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">No activity recorded yet.</p>
      ) : (
        <ol className="relative border-l border-slate-200 ml-2 space-y-4">
          {shown.map((a) => (
            <li key={a._id} className="ml-4">
              <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300 border-2 border-white" />
              <div className="text-sm text-slate-700">
                <b>{a.actorName}</b>
                {a.actorRole && a.actorRole !== "user" && (
                  <span className="ml-1 px-1.5 py-px rounded bg-slate-100 text-slate-500 text-[10px] font-semibold align-middle">{ROLE_LABEL[a.actorRole] || a.actorRole}</span>
                )}
                {a.actorRole === "user" && <span className="ml-1 text-[10px] text-slate-400">(customer)</span>}
                <span className="text-slate-500"> · {a.action}</span>
              </div>
              {a.details && <div className="text-xs text-slate-500 mt-0.5 break-words">{a.details}</div>}
              <div className="text-[11px] text-slate-400 mt-0.5">{fmtDateTime(a.at)}</div>
            </li>
          ))}
        </ol>
      )}
      {items.length > 8 && (
        <button onClick={() => setAll((v) => !v)} className="mt-3 text-sm font-semibold text-blue-600">
          {all ? "Show less" : `Show all ${items.length}`}
        </button>
      )}
    </section>
  );
}

/* --- Current quote + version history (Quote 1, Quote 2, ...) --- */
function QuotePanel({ request, latest, versions, showVersions, setShowVersions, onEdit }) {
  const [compareId, setCompareId] = useState(null);

  if (!latest) {
    return (
      <section className="border border-blue-100 bg-blue-50/40 rounded-2xl p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-white text-slate-700 shadow-sm"><FileText size={18} /></span>
          <div>
            <div className="font-bold text-slate-800">No quote yet</div>
            <div className="text-sm text-slate-500">Add parts and pricing, then send it to the customer.</div>
          </div>
        </div>
        <button onClick={onEdit}
          className="px-5 py-2.5 rounded-lg bg-slate-800 text-white font-semibold text-sm hover:bg-slate-900 shrink-0">
          Create Quote
        </button>
      </section>
    );
  }

  const numbered = numberVersions(versions);
  const current = numbered.find((v) => v._id === latest._id) || numbered[0];
  const previous = numbered.filter((v) => v._id !== current._id);
  const status = request.quoteStatus || current.status;
  const meta = ADMIN_QUOTE_STATUS[status] || ADMIN_QUOTE_STATUS[current.status] || ADMIN_QUOTE_STATUS.pending;
  const p = current.pricing || {};
  const daysLeft = status === "sent" ? quoteExpiry(request.quotedAt) : null;
  const comparing = previous.find((v) => v._id === compareId);

  return (
    <section className="border border-gray-100 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-slate-800 text-lg">Quote {current._v}</h3>
            {numbered.length > 1 && <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">current</span>}
            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase ${meta.cls}`}>{meta.label}</span>
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {current.quoteId} · {current.status === "draft" ? "saved" : "sent"} {current.quotedByName ? `by ${current.quotedByName} ` : ""}{fmtDateTime(current.updatedAt || current.createdAt)}
            {daysLeft != null && <> · {daysLeft > 0 ? `expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}` : "expired"}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {previous.length > 0 && (
            <button onClick={() => setCompareId(compareId ? null : previous[0]._id)}
              className="px-3 py-2 rounded-lg text-sm font-semibold text-blue-700 hover:bg-blue-50">
              {compareId ? "Hide changes" : `Compare with Quote ${previous[0]._v}`}
            </button>
          )}
          <button onClick={onEdit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <Pencil size={14} /> Edit Quote
          </button>
        </div>
      </div>

      {current.status === "draft" && request.quotedAt && previous[0] && (
        <p className="mt-3 text-xs bg-slate-100 text-slate-600 rounded-lg px-3 py-2">
          Quote {current._v} is a draft and hasn't been sent. The customer still sees Quote {previous[0]._v}.
        </p>
      )}

      {comparing && (
        <div className="mt-4">
          <QuoteCompare
            before={comparing}
            after={current}
            beforeLabel={`Quote ${comparing._v}`}
            afterLabel={`Quote ${current._v}${current.status === "draft" ? " (draft)" : ""}`}
            showInternal
            onClose={() => setCompareId(null)}
          />
        </div>
      )}

      <div className="mt-4 divide-y divide-gray-100 border-y border-gray-100">
        {(current.parts || []).map((part, i) => (
          <div key={i} className="flex items-center gap-3 py-3">
            <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden shrink-0">
              {part.image && <img src={assetUrl(part.image)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">{part.name || "Part"}</div>
              <div className="text-xs text-slate-500 truncate">
                {[part.condition, part.vehicle, part.partNumber && `#${part.partNumber}`, part.stock && `Stock ${part.stock}`].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="font-semibold text-slate-800">{money((part.quantity || 1) * (part.unitPrice || 0))}</div>
              <div className="text-xs text-slate-400">Qty {part.quantity || 1}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <div className="flex justify-between text-slate-500"><span>Parts</span><span>{money(p.subtotal)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Freight</span><span>{money(p.freightTotal)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Core charge</span><span>{money(p.coreChargeTotal)}</span></div>
      </div>
      <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
        <span className="font-bold text-slate-800">Total (AUD)</span>
        <span className="text-xl font-extrabold text-teal-800">{money(p.total)}</span>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
        <div><span className="text-slate-400">Warranty:</span> {current.warranty || "—"}</div>
        <div><span className="text-slate-400">Delivery:</span> {current.deliveryType || "—"}{current.estDelivery ? ` (est. ${fmtDate(current.estDelivery)})` : ""}</div>
      </div>
      {current.customerNote && <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3 whitespace-pre-line"><b>Customer note:</b> {current.customerNote}</p>}
      {current.internalNote && <p className="mt-2 text-sm text-amber-800 bg-amber-50 rounded-lg p-3 whitespace-pre-line"><b>Internal:</b> {current.internalNote}</p>}
      {current.attachments?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {current.attachments.map((a, i) => (
            <a key={i} href={assetUrl(a.url)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-50">
              <FileText size={13} className="text-slate-400" /> {a.name}
            </a>
          ))}
        </div>
      )}

      {previous.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowVersions((s) => !s)} className="flex items-center gap-2 text-sm font-semibold text-blue-600">
            <History size={14} /> {showVersions ? "Hide" : "Show"} earlier quotes ({previous.length})
          </button>
          {showVersions && (
            <div className="mt-2 space-y-2">
              {previous.map((v) => {
                const was = replacedFrom(v);
                const m = ADMIN_QUOTE_STATUS[v.status] || ADMIN_QUOTE_STATUS.pending;
                return (
                  <div key={v._id} className={`rounded-lg px-3 py-2 text-sm ${compareId === v._id ? "bg-blue-50" : "bg-slate-50"}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <b className="text-slate-800">Quote {v._v}</b>
                        <span className="text-slate-500"> · {fmtDateTime(v.createdAt)}</span>
                        {v.quotedByName && <span className="text-slate-500"> · by {v.quotedByName}</span>}
                        <div className="text-xs text-slate-400">{(v.parts || []).map((x) => x.name).filter(Boolean).join(", ")}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-slate-700">{money(v.pricing?.total)}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${m.cls}`}>
                          {m.label}{was ? ` · was ${ADMIN_QUOTE_STATUS[was]?.label.toLowerCase() || was}` : ""}
                        </span>
                        <button onClick={() => setCompareId(compareId === v._id ? null : v._id)} className="text-xs font-semibold text-blue-700 hover:underline">
                          {compareId === v._id ? "Hide" : `Compare with Quote ${current._v}`}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Attachment({ a }) {
  if (a.type === "image") return <a href={assetUrl(a.url)} target="_blank" rel="noreferrer"><img src={assetUrl(a.url)} alt={a.name} className="max-w-[200px] rounded-xl border" /></a>;
  if (a.type === "audio") return <audio src={assetUrl(a.url)} controls className="h-9" />;
  if (a.type === "video") return <video src={assetUrl(a.url)} controls className="max-w-[240px] rounded-xl" />;
  return (
    <a href={assetUrl(a.url)} download target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-2 p-2 rounded-lg border border-gray-100 hover:bg-slate-50 text-sm bg-white">
      <FileText size={16} className="text-red-500" /> {a.name} <Download size={14} className="text-slate-400" />
    </a>
  );
}

/* --- Voice note player with a play/pause toggle --- */
// function VoiceNote({ src, index }) {
//   const audioRef = useRef(null);
//   const [playing, setPlaying] = useState(false);
//   const toggle = () => {
//     const a = audioRef.current;
//     if (!a) return;
//     if (playing) a.pause(); else a.play();
//   };
//   return (
//     <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-gray-100">
//       <button onClick={toggle} aria-label={playing ? "Pause" : "Play"}
//         className="grid place-items-center w-9 h-9 rounded-full bg-red-500 text-white shrink-0">
//         {playing ? <Pause size={16} /> : <Play size={16} />}
//       </button>
//       <div className="flex-1">
//         <div className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Mic size={14} /> Voice note {index}</div>
//         {/* {audioRef} */}
//         {src}
//         {/* <audio ref={audioRef} src={src}
//           onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
//           controls className="w-full mt-1 h-8" /> */}
//         <audio
//           ref={audioRef}
//           controls
//           preload="metadata"
//           className="w-full mt-1 h-8"
//           onPlay={() => setPlaying(true)}
//           onPause={() => setPlaying(false)}
//           onEnded={() => setPlaying(false)}
//           onError={(e) => {
//             const audio = e.currentTarget;

//             console.error("VOICE AUDIO ERROR", {
//               src,
//               errorCode: audio.error?.code,
//               errorMessage: audio.error?.message,
//               networkState: audio.networkState,
//               readyState: audio.readyState,
//             });
//           }}
//         >
//           <source src={src} type="audio/mp4" />
//           Your browser does not support this audio file.
//         </audio>
//       </div>
//     </div>
//   );
// }

function VoiceNote({ src, index }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");

  const toggle = async () => {
    const audio = audioRef.current;

    if (!audio) return;

    try {
      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
      }
    } catch (err) {
      console.error("Audio play error:", err);
      setError("Unable to play this voice note.");
    }
  };

  return (
    <div className="flex items-center gap-3 bg-slate-50 rounded-xl px-4 py-3 border border-gray-100">

      {/* Play Button */}
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        className="grid place-items-center w-9 h-9 rounded-full bg-red-500 text-white shrink-0"
      >
        {playing ? (
          <Pause size={16} />
        ) : (
          <Play size={16} />
        )}
      </button>


      <div className="flex-1 min-w-0">

        <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Mic size={14} />
          Voice note {index}
        </div>


        <audio
          ref={audioRef}
          src={src}
          controls
          preload="metadata"
          className="w-full mt-1 h-8"

          onLoadedMetadata={(e) => {
            console.log(
              "Audio loaded:",
              src,
              "Duration:",
              e.currentTarget.duration
            );

            setError("");
          }}

          onCanPlay={() => {
            setError("");
          }}

          onPlay={() => {
            setPlaying(true);
          }}

          onPause={() => {
            setPlaying(false);
          }}

          onEnded={() => {
            setPlaying(false);
          }}

          onError={(e) => {
            const audio = e.currentTarget;

            console.error(
              "VOICE AUDIO ERROR",
              {
                src,
                code: audio.error?.code,
                message: audio.error?.message,
                networkState: audio.networkState,
                readyState: audio.readyState
              }
            );

            setPlaying(false);
            setError("Unable to load this voice note.");
          }}
        />

        {error && (
          <div className="text-xs text-red-500 mt-1">
            {error}
          </div>
        )}

      </div>

    </div>
  );
}